import type { Hand } from 'ErgoBot';
import type { Pitch, PrerenderedPhraseData } from 'Types';
import { noteIdOf } from './noteFingering';

type PrerenderedNote = PrerenderedPhraseData['currentVoiceEntries'][number]['Notes'][number];

/** One hand's notes and rests of one phrase, as the fingering runtime reads them. */
export interface HandLine {
  /** A written pitch name per note, null per rest, in time order. */
  line: Array<string | null>;
  /** The drawn note behind each note of the line, rests left out. */
  noteIds: Array<string | undefined>;
}

/** Each hand's line; null for a hand whose staff cannot be read as one. */
export type HandLines = Record<Hand, HandLine | null>;

const STAFF_OF_HAND: Record<Hand, string> = { rh: 'top', lh: 'bottom' };
const LETTER_OF_PITCH_CLASS: Readonly<Record<number, string>> = {
  0: 'C',
  2: 'D',
  4: 'E',
  5: 'F',
  7: 'G',
  9: 'A',
  11: 'B',
};
const ACCIDENTAL_OF_ALTERATION: Readonly<Record<number, string>> = { [-1]: 'b', 0: '', 1: '#' };

const midiOf = (pitch: Pitch): number => Math.round(pitch.halfTone + 12);

/**
 * The name of a pitch as the fingering tables spell it (C4, F#4, Bb3), or null
 * for a spelling they have no name for, such as a double sharp.
 */
export const writtenPitchName = (pitch: Pitch): string | null => {
  const midi = midiOf(pitch);
  const alteration = ((((midi - pitch.fundamentalNote + 6) % 12) + 12) % 12) - 6;
  const letter = LETTER_OF_PITCH_CLASS[pitch.fundamentalNote];
  const accidental = ACCIDENTAL_OF_ALTERATION[alteration];
  if (letter === undefined || accidental === undefined) return null;
  const octave = (midi - alteration - pitch.fundamentalNote) / 12 - 1;
  return `${letter}${accidental}${octave}`;
};

const notesOnStaff = (phraseSlice: PrerenderedPhraseData, staff: string): PrerenderedNote[] =>
  phraseSlice.currentVoiceEntries
    .flatMap((voiceEntry) => voiceEntry.Notes)
    .filter((note) => note.staffPosition === staff);

/** What each slice of time holds on this staff. */
const staffEventsOf = (written: PrerenderedPhraseData[], staff: string): PrerenderedNote[][] =>
  written
    .map((phraseSlice) => notesOnStaff(phraseSlice, staff))
    .filter((events) => events.length > 0);

const handLineOf = (written: PrerenderedPhraseData[], staff: string): HandLine | null => {
  const events = staffEventsOf(written, staff);
  // The runtime fingers one note at a time: a staff with two things at once
  // (a chord, a second voice) is not a line it can read.
  if (events.some((event) => event.length !== 1)) return null;
  const line = events.map(([note]) => (note.isRest ? null : writtenPitchName(note.pitch)));
  const isUnnamedNote = events.some(([note], place) => !note.isRest && line[place] === null);
  if (isUnnamedNote) return null;
  return {
    line,
    noteIds: events.filter(([note]) => !note.isRest).map(([note]) => noteIdOf(note)),
  };
};

/**
 * Each hand's line of a phrase as it is written: the treble staff is the right
 * hand's, the bass staff the left's. What the notes sound as is not read here:
 * the runtime works that out from the key.
 */
export const handLinesOf = (written: PrerenderedPhraseData[]): HandLines => ({
  rh: handLineOf(written, STAFF_OF_HAND.rh),
  lh: handLineOf(written, STAFF_OF_HAND.lh),
});
